import { Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircleIcon,
  DocumentCheckIcon,
  DocumentMagnifyingGlassIcon,
  ExclamationCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

import { useMessageWithProcessSpecification } from 'services/repositories/messages/messages';
import { cn } from 'support/helpers/cn/cn';
import { EMPTY_FIELD } from 'support/helpers/const/const';
import { routeToTransactionPage } from 'support/helpers/navigation/navigation';
import { translationForDocumentTypeWithVirtual } from 'support/helpers/transactions/transactions';
import {
  DocumentTypeWithVirtual,
  MessageDTO,
  PartnerDTO,
  TransactionInternalType,
  TransactionState,
  ValueOf,
} from 'support/types';
import { InProgresCircle } from 'components/CustomIcons/InProgressCircle';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Display/Tooltip/Tooltip';

type RelatedMessageType = {
  id: string;
  type?: ValueOf<DocumentTypeWithVirtual> | null;
  externalMessageRef?: string | null;
  createdAt: string;
  sender?: PartnerDTO;
  receiver?: PartnerDTO;
  state: MessageDTO['state'];
};

type RelatedMessageProps = {
  relatedMessage: Pick<RelatedMessageType, 'id' | 'externalMessageRef' | 'type' | 'createdAt' | 'state'>;
  active: boolean;
};
type IconForMessageProps = Pick<RelatedMessageProps['relatedMessage'], 'state'> & {
  active: boolean;
  ref?: Ref<HTMLDivElement>;
};

export function RelatedMessage({ relatedMessage, active }: RelatedMessageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Prefetch the message with process specification
  useMessageWithProcessSpecification({ variables: { messageId: relatedMessage.id } });

  return (
    <li key={relatedMessage.id}>
      <div
        className={cn(
          'group -mx-2 flex items-center rounded-md px-2 py-1.5',
          active ? 'cursor-default bg-gray-100' : 'cursor-pointer',
        )}
        data-testid={`LinkToTransaction-${relatedMessage.id}`}
        onClick={() => {
          navigate(routeToTransactionPage(TransactionInternalType.message, relatedMessage.id), {
            state: { keepBreadcrumbs: true },
          });
        }}
      >
        <IconForMessage state={relatedMessage.state} active={active} />
        <div className="ml-2 w-full">
          <div className="grid w-full grid-cols-2 gap-1 text-xs">
            <span
              className={cn(
                'self-center overflow-hidden text-ellipsis',
                active
                  ? 'font-medium text-gray-900'
                  : 'text-gray-500 group-hover:font-medium group-hover:text-gray-900',
              )}
            >
              {translationForDocumentTypeWithVirtual(t, relatedMessage.type)}
            </span>

            <span
              dir="rtl"
              className={cn(
                'm-auto w-full truncate text-start',
                active
                  ? 'font-medium text-gray-900'
                  : 'text-gray-500 group-hover:font-medium group-hover:text-gray-900',
              )}
            >
              <Tooltip enabled={Boolean(relatedMessage.externalMessageRef)} placement="top-end">
                <TooltipTrigger>
                  <span>{relatedMessage.externalMessageRef || EMPTY_FIELD}</span>
                </TooltipTrigger>
                <TooltipContent>{relatedMessage.externalMessageRef}</TooltipContent>
              </Tooltip>
            </span>
          </div>
        </div>
      </div>
    </li>
  );
}

const IconForMessage = ({ state, active, ref }: IconForMessageProps) => {
  let icon = null;
  let classes;
  switch (state) {
    case TransactionState.success:
      icon = <CheckCircleIcon className="size-5" data-testid="CheckCircleIcon" />;
      classes = active ? 'text-procuros-green-600 border-procuros-green-200' : 'group-hover:text-procuros-green-500';
      break;
    case TransactionState.pending:
      icon = <InProgresCircle className="size-5" data-testid="InProgresCircle" />;
      classes = active ? 'text-yellow-600 border-yellow-200' : 'group-hover:text-yellow-500';
      break;

    case TransactionState.failed:
      icon = <ExclamationCircleIcon className="size-5" data-testid="ExclamationCircleIcon" />;
      classes = active ? 'text-red-600 border-red-200' : 'group-hover:text-red-500';
      break;

    case TransactionState.missing:
      icon = <DocumentMagnifyingGlassIcon className="size-5" data-testid="DocumentMagnifyingGlassIcon" />;
      classes = active ? 'text-yellow-600 border-yellow-200' : 'group-hover:text-yellow-500';
      break;

    case TransactionState.pending_review:
      icon = <DocumentCheckIcon className="size-5" data-testid="DocumentCheckIcon" />;
      classes = active ? 'text-sky-600 border-sky-200' : 'group-hover:text-sky-500';
      break;

    case TransactionState.dropped:
    case TransactionState.superseded:
    case TransactionState.unknown:
      icon = <XCircleIcon className="size-5" data-testid="XCircleIcon" />;
      classes = active ? 'text-gray-600 border-gray-200' : 'group-hover:text-gray-500';
      break;
  }

  return (
    <span className={cn('relative inline-block text-gray-400', classes)} ref={ref}>
      {icon}
    </span>
  );
};
